import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, FileText, Eye, Download, Trash2, Send, Filter } from 'lucide-react';
import { documentApi } from '../services/api';
import { formatRelativeTime, formatFileSize } from '@pdfsign/shared/utils';
import { Document, DocumentStatus } from '@pdfsign/shared/types';

import { toast, confirmDialog } from '../stores/feedbackStore';

const statusConfig: Record<DocumentStatus, { label: string; color: string }> = {
  draft: { label: 'Nháp', color: 'badge-gray' },
  pending_sign: { label: 'Chờ ký', color: 'badge-warning' },
  in_progress: { label: 'Đang ký', color: 'badge-primary' },
  completed: { label: 'Hoàn tất', color: 'badge-success' },
  rejected: { label: 'Từ chối', color: 'badge-danger' },
  expired: { label: 'Hết hạn', color: 'badge-gray' },
  cancelled: { label: 'Đã hủy', color: 'badge-gray' },
};

const typeLabels: Record<string, string> = {
  bao_cao: 'Báo cáo',
  quyet_dinh: 'Quyết định',
  to_trinh: 'Tờ trình',
  cong_van: 'Công văn',
  hop_dong: 'Hợp đồng',
  khac: 'Khác',
};

export default function DocumentListPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await documentApi.list({
        page,
        limit: 20,
        status: status ? [status] : undefined,
        type: type ? [type] : undefined,
        search: search || undefined,
      });
      setDocuments(res.data.data);
      setTotal(res.data.meta?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchDocuments, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, type, search]);

  const handleDelete = async (id: string) => {
    const ok = await confirmDialog({
      title: 'Xóa tài liệu',
      message: 'Bạn có chắc chắn muốn xóa tài liệu nháp này?',
      confirmText: 'Xóa',
      cancelText: 'Hủy',
      type: 'danger',
    });
    if (!ok) return;

    try {
      await documentApi.delete(id);
      toast.success('Đã xóa tài liệu nháp thành công');
      fetchDocuments();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Không thể xóa tài liệu');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-slate-200/80 dark:border-white/10">
        <div>
          <h1 className="text-lg sm:text-xl font-medium tracking-tight text-slate-900 dark:text-white">
            Danh sách tài liệu
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-normal mt-0.5">
            Tất cả tài liệu bạn đã tạo hoặc được giao ký số
          </p>
        </div>
        <Link to="/documents/create" className="btn-primary inline-flex items-center gap-1.5 text-xs h-8 px-3 font-medium">
          <Plus className="w-3.5 h-3.5" />
          <span>Tạo tài liệu mới</span>
        </Link>
      </div>

      <div className="card p-2.5 sm:p-3">
        <div className="flex flex-col sm:flex-row gap-2.5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="input pl-9 h-8 text-xs font-normal"
              placeholder="Tìm theo tiêu đề tài liệu, tên file..."
            />
          </div>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="input sm:w-44 text-xs font-normal cursor-pointer h-8 py-0">
            <option value="">Tất cả trạng thái</option>
            {Object.entries(statusConfig).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="input sm:w-40 text-xs font-normal cursor-pointer h-8 py-0">
            <option value="">Tất cả loại văn bản</option>
            {Object.entries(typeLabels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center text-slate-400 text-xs font-normal">Đang tải danh sách tài liệu...</div>
      ) : documents.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-slate-100 dark:bg-slate-800/80 flex items-center justify-center text-slate-400 border border-slate-200 dark:border-white/10">
            <FileText className="w-6 h-6" />
          </div>
          <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-1">Không tìm thấy tài liệu nào</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 max-w-sm mx-auto font-normal">Thử thay đổi bộ lọc tìm kiếm hoặc tạo một tài liệu ký mới.</p>
          <Link to="/documents/create" className="btn-primary inline-flex items-center gap-1.5 text-xs h-8 px-3 font-medium">
            <Plus className="w-3.5 h-3.5" /> Tạo tài liệu
          </Link>
        </div>
      ) : (
        <div className="card divide-y divide-slate-100 dark:divide-white/5 overflow-hidden">
          {documents.map((doc) => {
            const cfg = statusConfig[doc.status as DocumentStatus] || statusConfig.draft;
            return (
              <div key={doc.id} className="py-2.5 px-3.5 sm:px-4 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors flex flex-col sm:flex-row sm:items-center gap-3">
                <Link to={`/documents/${doc.id}`} className="flex items-center gap-3 flex-1 min-w-0 group">
                  <div className="w-8 h-8 rounded-lg bg-sky-50 dark:bg-sky-500/10 border border-sky-200/60 dark:border-sky-500/20 flex items-center justify-center flex-shrink-0 text-sky-600 dark:text-sky-400">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-normal text-slate-800 dark:text-slate-200 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors truncate text-xs sm:text-sm">
                      {doc.title}
                    </p>
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5 font-normal">
                      {typeLabels[doc.type] || doc.type} · {formatFileSize(doc.file_size)} · {formatRelativeTime(doc.updated_at)}
                    </p>
                  </div>
                </Link>
                <div className="flex items-center gap-2.5 flex-shrink-0">
                  <span className={`badge ${cfg.color} text-[10px] font-normal`}>{cfg.label}</span>
                  <span className="text-[11px] font-mono text-slate-400 hidden sm:inline font-normal">{doc.current_step}/{doc.total_steps}</span>
                  <Link to={`/documents/${doc.id}`} className="p-1.5 text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Chi tiết">
                    <Eye className="w-3.5 h-3.5" />
                  </Link>
                  {doc.status === 'draft' && (
                    <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors" title="Xóa">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {total > 20 && (
            <div className="p-4 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-900/30 border-t border-slate-100 dark:border-white/5">
              <p>Trang {page} / {Math.ceil(total / 20)}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-outline py-1 px-2.5 text-xs">Trước</button>
                <button onClick={() => setPage(p => p + 1)} disabled={page * 20 >= total} className="btn-outline py-1 px-2.5 text-xs">Sau</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}